import { useRoute } from 'vue-router';

import useIteratively from '@/composables/useIteratively';
import {
  CUSTOM,
  PARTNER_RULE,
  POPULATION_RULE,
  RECOMMENDED,
} from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import { useDataSharePresetsStore, useSourcesStore } from '@/stores';
import type { Population, SegmentType } from '@/types/populations';
import { getFeedIdForPopulation } from '@/utils/getFeedIdForPopulation';

/**
 * Context for trackDataSharingChangeFromContext: current/previous values and event metadata.
 * The helper derives feedId, sourceId, recommendedPresetId, sharedFields, and changed from
 * the population and stores.
 */
export type DataSharingTrackingContext = {
  /** Population (or undefined to no-op). Used for feedId, sourceId, segment, populationId. */
  population: Population | undefined;
  current: { visibility: string; presetId: number | null };
  previous?: { visibility?: string; presetId?: number | null };
  eventSite: string;
  bulk?: boolean;
  partnerId?: number | null;
  partnerPopulationIds?: number[];
};

type TrackDataSharingParams = {
  populationId: number;
  visibility: string;
  presetId?: number | null;
  segment?: SegmentType;
  partnerId?: number | null;
  partnerPopulationIds?: number[];
  previousVisibility?: string;
  previousPresetId?: number | null;
  recommendedPresetId?: number | null;
  /** Only PopulationSharingModal passes true, when editing multiple populations or partners at once; all other callers pass false or omit */
  bulk?: boolean;
  /** Property names that actually changed (e.g. ['visibility_level', 'preset_type']) */
  changed?: string[];
  /** Field IDs or names being shared (when available) */
  shared_fields?: string[];
  /** Required: which UI surface triggered the save (e.g. EVENT_SITES.SHARING_SETTINGS_MODAL) */
  eventSite: string;
  /** Previous rule type when it changed (population | partner); when only visibility changed, omit and composable uses current sharing_level for previous_sharing_level */
  previousRuleType?: 'population' | 'partner';
};

export default function useDataSharingTracking() {
  const { iteratively } = useIteratively();
  const route = useRoute();
  const sourcesStore = useSourcesStore();
  const dataSharePresetsStore = useDataSharePresetsStore();

  /**
   * Tracks a data sharing save using population and store context. Derives feedId, sourceId,
   * recommendedPresetId, sharedFields, and changed from the population and current/previous values.
   * Use this instead of repeating the same ~20 lines at each save site.
   */
  function trackDataSharingChangeFromContext(
    context: DataSharingTrackingContext,
  ) {
    const {
      population,
      current,
      previous,
      eventSite,
      bulk,
      partnerId,
      partnerPopulationIds,
    } = context;
    if (!population) return;

    const feedId = getFeedIdForPopulation(population, (id) =>
      sourcesStore.getSourceById(id),
    );
    const sourceId =
      isGoogleOrCsvSource(population.integration_type ?? '') &&
      population.source_id
        ? population.source_id
        : undefined;
    const recommendedPresetId =
      feedId !== undefined
        ? dataSharePresetsStore.getCrossbeamPresetId(feedId, sourceId)
        : undefined;
    const sharedFields =
      feedId !== undefined && current.presetId != null
        ? dataSharePresetsStore
            .getSourceFieldIdsByPresetId(feedId, sourceId, current.presetId)
            .map(String)
        : [];

    const changedProps: string[] = [];
    if (previous) {
      if (previous.visibility !== current.visibility) {
        changedProps.push('visibility_level');
      }
      if (previous.presetId !== current.presetId) {
        changedProps.push('preset_type');
      }
    } else {
      changedProps.push('visibility_level');
      if (current.presetId != null) {
        changedProps.push('preset_type');
      }
    }

    trackDataSharingChange({
      populationId: population.id,
      visibility: current.visibility,
      presetId: current.presetId,
      segment: population.segment ?? undefined,
      partnerId,
      partnerPopulationIds,
      previousVisibility: previous?.visibility,
      previousPresetId: previous?.presetId ?? null,
      recommendedPresetId,
      eventSite,
      bulk,
      changed: changedProps,
      shared_fields: sharedFields,
    });
  }

  function trackDataSharingChange(params: TrackDataSharingParams) {
    const {
      populationId,
      visibility,
      presetId,
      segment,
      partnerId,
      partnerPopulationIds = [],
      previousVisibility,
      previousPresetId,
      recommendedPresetId,
      bulk,
      changed = [],
      shared_fields = [],
      eventSite,
      previousRuleType,
    } = params;

    let routeSource = 'default-sharing';
    const routeName = String(route.name || '');

    if (routeName.includes('onboarding')) {
      routeSource = 'onboarding';
    } else if (routeName.includes('population')) {
      routeSource = 'populations';
    } else if (routeName.includes('partner')) {
      routeSource = 'partners';
    }

    const sharingLevel = partnerId ? PARTNER_RULE : POPULATION_RULE;

    const getPresetType = (currentPresetId?: number | null) => {
      if (!currentPresetId) return undefined;
      if (!recommendedPresetId) return CUSTOM;
      return currentPresetId === recommendedPresetId ? RECOMMENDED : CUSTOM;
    };

    const partnerPopulationIdsStr = partnerPopulationIds.map((id) =>
      id.toString(),
    );

    const previousSharingLevel =
      previousRuleType !== undefined
        ? previousRuleType
        : previousVisibility !== undefined
          ? sharingLevel
          : undefined;

    const payload: Parameters<typeof iteratively.userDefinedDataSharing>[0] = {
      event_site: eventSite,
      partner_population_id: partnerPopulationIdsStr,
      population_id: populationId.toString(),
      population_type: segment,
      preset_type: getPresetType(presetId),
      previous_sharing_level: previousSharingLevel,
      previous_visibility_level: previousVisibility,
      sharing_level: sharingLevel,
      changed,
      shared_fields,
      metadata: {
        previous_preset_type: getPresetType(previousPresetId),
      },
      source: routeSource,
      visibility_level: visibility,
    };

    if (partnerId !== undefined && partnerId !== null) {
      payload.partner_id = String(partnerId);
    }
    if (bulk !== undefined) {
      payload.bulk = bulk;
    }

    iteratively.userDefinedDataSharing(payload);
  }

  return {
    trackDataSharingChange,
    trackDataSharingChangeFromContext,
  };
}
